import { ChangeEvent, useLayoutEffect, useRef } from "react";
import { maskPhoneInput, toPhoneDigits } from "../helper/phoneUtils";

type PhoneInputProps = {
  /** Stays on the real <input> — the forms find these fields with querySelector('[name="phone"]'). */
  name: string;
  /** Bare digits, exactly as the value is held in form state and sent to the API. */
  value?: string | number | null;
  /** Receives an event whose target.value is bare digits, never the masked text. */
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
};

/**
 * Masked phone entry: shows "(406) 555-0120" while the value that leaves the component —
 * and therefore the value the forms validate and POST — stays bare digits.
 *
 * Pasting a formatted number works, which the raw inputs this replaces could not do: they
 * capped length or rejected punctuation before stripping, so "(406) 555-0120" was truncated
 * or dropped outright.
 *
 * Deliberately no maxLength: browsers apply it to pasted text too, so "+1 406 555 0120"
 * would be clipped to 14 characters before this component ever saw it — reintroducing the
 * very truncation bug this replaces. toPhoneDigits caps the value at 10 digits instead, and
 * the rendered mask can never be longer than PHONE_MASK_LENGTH anyway.
 */
const PhoneInput = ({
  name,
  value,
  onChange,
  className = "form-control",
  placeholder = "(555) 123-4567",
  required = false,
  disabled = false,
  id,
}: PhoneInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  /** Digits to the left of the caret; re-applied after the mask re-renders. */
  const caretDigitsRef = useRef<number | null>(null);

  const digits = toPhoneDigits(value);
  const masked = maskPhoneInput(digits);

  // The mask changes the string length on nearly every keystroke, so React restores the caret
  // to the end of the value. Re-deriving it from a digit count keeps mid-string edits usable.
  useLayoutEffect(() => {
    const input = inputRef.current;
    const caretDigits = caretDigitsRef.current;
    if (!input || caretDigits === null) {
      return;
    }
    caretDigitsRef.current = null;

    const offset = Math.min(caretOffsetForDigits(caretDigits), input.value.length);
    input.setSelectionRange(offset, offset);
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const caret = event.target.selectionStart ?? raw.length;
    let nextDigits = toPhoneDigits(raw);
    let digitsBeforeCaret = countDigits(raw.slice(0, caret));

    // Backspacing onto a separator (the ")" or "-") deletes only that character, which leaves
    // the digits untouched and the mask unchanged — the caret would stall. Delete the digit
    // in front of it instead, which is what the user meant.
    const isDeletion = raw.length < masked.length;
    if (isDeletion && nextDigits === digits && digitsBeforeCaret > 0) {
      nextDigits =
        nextDigits.slice(0, digitsBeforeCaret - 1) + nextDigits.slice(digitsBeforeCaret);
      digitsBeforeCaret -= 1;
    }

    caretDigitsRef.current = digitsBeforeCaret;

    onChange({
      ...event,
      target: { ...event.target, name, value: nextDigits, type: "tel" },
    } as unknown as ChangeEvent<HTMLInputElement>);
  };

  return (
    <input
      ref={inputRef}
      id={id}
      type="tel"
      inputMode="tel"
      name={name}
      className={className}
      placeholder={placeholder}
      value={masked}
      onChange={handleChange}
      required={required}
      disabled={disabled}
    />
  );
};

const countDigits = (value: string): number => value.replace(/\D/g, "").length;

/**
 * Where the caret belongs in "(ddd) ddd-dddd" once `count` digits sit to its left —
 * just past the separators that precede it.
 */
const caretOffsetForDigits = (count: number): number => {
  if (count <= 0) {
    return 1; // just inside the opening paren
  }
  if (count <= 3) {
    return count + 1;
  }
  if (count <= 6) {
    return count + 3;
  }
  return count + 4;
};

export default PhoneInput;
